import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useRouter } from 'next/router'

import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { type Locale } from '@lib/language'
import { getShopifyCartIdStorageKey } from '@lib/local-storage'
import { getLinkPageUrl } from '@lib/routes'
import { type LocalizedShop, ShopContext } from '@lib/shop-context'
import { emptyCart, getOrCreateCart } from './helpers'
import { type Cart } from './types'

interface CartContextProps {
  cart: Cart
  isCartModalOpen: boolean
  isCartUpdating: boolean
  isCreatingProduct: boolean
  cartAddingProductIds: number[]
  isCartSubmitting: boolean
  openCartInSeparatePage?: boolean
  toggleCart: (newState: boolean) => void
  saveCart: (cart?: Cart) => void
  setIsCartUpdating: (isCartUpdating: boolean) => void
  setIsCreatingProduct: (isCreatingProduct: boolean) => void
  setCartAddingProductIds: (cartAddingProductIds: number[]) => void
  setIsCartSubmitting: (isCartSubmitting: boolean) => void
}

interface CartContextProviderProps {
  site: SanitySiteFragment
  localizedShop: LocalizedShop
  locale: Locale
  children: ReactNode
}

export const CartContext = createContext<CartContextProps>({
  cart: emptyCart,
  isCartModalOpen: false,
  isCartUpdating: false,
  isCreatingProduct: false,
  cartAddingProductIds: [],
  isCartSubmitting: false,
  toggleCart: () => null,
  saveCart: () => null,
  setIsCartUpdating: () => null,
  setIsCreatingProduct: () => null,
  setCartAddingProductIds: () => null,
  setIsCartSubmitting: async () => null,
})

/**
 * The cart context provider.
 */
export const CartContextProvider = ({
  site,
  localizedShop,
  locale,
  children,
}: CartContextProviderProps) => {
  const { shopifyStorefrontGraphQlClient } = useContext(ShopContext)

  const [cart, setCart] = useState<Cart>(emptyCart)
  const [isCartModalOpen, setIsCartModalOpen] = useState(false)
  const [isCartUpdating, setIsCartUpdating] = useState(false)
  const [isCreatingProduct, setIsCreatingProduct] = useState(false)
  const [cartAddingProductIds, setCartAddingProductIds] = useState<number[]>([])
  const [isCartSubmitting, setIsCartSubmitting] = useState(false)
  const [localeInitialised, setLocaleInitialised] = useState('')

  const router = useRouter()

  // Sanity settings
  const openCartInSeparatePage = useMemo(
    () => !!site.cartSettings?.openInSeparatePage,
    [site],
  )

  const saveCart = useCallback(
    (cart?: Cart) => {
      if (!cart) {
        return
      }

      setCart(cart)

      if (typeof window !== 'undefined') {
        const shopifyCartIdStorageKey = getShopifyCartIdStorageKey(locale)
        localStorage.setItem(shopifyCartIdStorageKey, cart.id)
      }
    },
    [locale],
  )

  const toggleCart = useCallback(
    (open?: boolean) => {
      // Show cart page
      if (openCartInSeparatePage && (typeof open === 'undefined' || open)) {
        const url = getLinkPageUrl({
          pageType: 'cartPage',
        })
        router.push(url, url, {
          locale,
        })

        return
      }

      // Show/hide cart modal
      setIsCartModalOpen((currentIsCartModalOpen) =>
        typeof open !== 'undefined' ? open : !currentIsCartModalOpen,
      )
    },
    [locale, openCartInSeparatePage, router],
  )

  // Load cart from Shopify
  useEffect(() => {
    const loadCart = async () => {
      if (
        !localizedShop.isLoadingFinished ||
        !localizedShop.shopifyShopAndCart ||
        localeInitialised === locale ||
        localizedShop.locale !== locale
      ) {
        return
      }

      if (!shopifyStorefrontGraphQlClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      setLocaleInitialised(locale)

      const currentCart = await getOrCreateCart(
        locale,
        shopifyStorefrontGraphQlClient,
        localizedShop.shopifyShopAndCart,
      )
      saveCart(currentCart)
    }

    loadCart()
  }, [
    locale,
    localeInitialised,
    saveCart,
    localizedShop,
    shopifyStorefrontGraphQlClient,
  ])

  return (
    <CartContext.Provider
      value={{
        cart,
        isCartModalOpen,
        isCartUpdating,
        isCreatingProduct,
        cartAddingProductIds,
        isCartSubmitting,
        openCartInSeparatePage,
        toggleCart,
        saveCart,
        setIsCartUpdating,
        setIsCreatingProduct,
        setCartAddingProductIds,
        setIsCartSubmitting,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}
